import type { IInvoiceDetail } from './types';

/**
 * The customer's failed/pending payment: the most recent invoice, and only when it is still open
 * with a balance.
 *
 * Deliberately the latest invoice rather than any open one. Retry pays the subscription's
 * `latest_invoice`, so surfacing an older open invoice would show one amount and pay another —
 * and when the newer invoice is already paid, retry reports success while the invoice on screen
 * stays open, letting the customer "succeed" indefinitely without being charged. Older open
 * invoices (e.g. left behind by a cancelled subscription) are settled through Stripe's hosted
 * invoice page, not this alert.
 *
 * Ordered here rather than trusting the API's ordering, so the pairing with `latest_invoice`
 * cannot silently break if that order changes.
 *
 * Kept in a leaf module (types are erased at runtime) so it can be tested without loading the
 * store graph, which imports back into this one.
 */
export function selectUnpaidInvoice(invoices: IInvoiceDetail[]): IInvoiceDetail | undefined {
  const [latest] = [...invoices].sort((a, b) => b.created - a.created);
  return latest && latest.status === 'open' && latest.amountRemaining > 0 ? latest : undefined;
}
